import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import supabaseClient from '@/api/supabaseClient';
import { RealtimeChannel } from '@supabase/supabase-js';

// Timeout for Realtime subscription (10 minutes - extraction with GPT-4o can be slow)
const REALTIME_TIMEOUT_MS = 600000;

export interface UploadMarkSchemeParams {
  pastPaperId: string;
  pdfUrl: string;
  file: File;
}

export interface UploadMarkSchemeResult {
  pastPaperId: string;
}

/**
 * Generate the mark scheme file path from the base PDF URL
 */
const generateMarkSchemeFilePath = (pdfUrl: string): string => {
  return pdfUrl.replace('.pdf', '-mark-scheme.pdf');
};

/**
 * Call edge function - completion is detected via Realtime subscription
 * Only reject on 4xx validation errors; ignore 5xx timeouts (function keeps running)
 */
const invokeEdgeFunction = async (
  functionName: string,
  body: Record<string, unknown>,
  onValidationError: (error: Error) => void
): Promise<void> => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const { data: sessionData } = await supabaseClient.auth.getSession();
  const accessToken = sessionData?.session?.access_token;

  if (!accessToken) {
    throw new Error('Not authenticated');
  }

  // Fire and forget - don't await! We rely on Realtime subscription for completion.
  // Using .then()/.catch() instead of await so the function returns immediately.
  fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  }).then(async (response) => {
    if (!response.ok) {
      // Only fail on 4xx (validation errors) - 5xx means function is still running
      if (response.status >= 400 && response.status < 500) {
        try {
          const data = await response.json();
          const errorMessage = data?.errors?.join(', ') || 'Validation failed';
          console.error('Validation error:', errorMessage);
          onValidationError(new Error(errorMessage));
        } catch {
          onValidationError(new Error('Failed to process mark scheme'));
        }
      } else {
        console.log(`Gateway timeout (${response.status}) - function still running, waiting for Realtime...`);
      }
    }
  }).catch((error) => {
    console.log('Fetch error (function may still be running):', error);
  });
};

interface RealtimeWaitResult {
  promise: Promise<void>;
  rejectWithError: (error: Error) => void;
  cleanup: () => void;
}

/**
 * Set up Realtime subscription for mark scheme processing completion
 * Returns a promise that resolves when mark_scheme_processed_at is updated
 */
const setupRealtimeWait = (pastPaperId: string, timeoutMs: number): RealtimeWaitResult => {
  let channel: RealtimeChannel | null = null;
  let timeoutId: NodeJS.Timeout | null = null;
  let rejectFn: ((error: Error) => void) | null = null;

  const cleanup = () => {
    if (timeoutId) clearTimeout(timeoutId);
    if (channel) {
      supabaseClient.removeChannel(channel);
      channel = null;
    }
  };

  const promise = new Promise<void>((resolve, reject) => {
    rejectFn = reject;

    // Set timeout
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('Mark scheme processing is taking longer than expected. Please check back in a few minutes.'));
    }, timeoutMs);

    // Subscribe to UPDATE events on past_papers table for this specific paper
    channel = supabaseClient
      .channel(`mark-scheme-processing-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'past_papers',
          filter: `id=eq.${pastPaperId}`,
        },
        (payload) => {
          const updatedPaper = payload.new as { id: string; mark_scheme_processed_at: string | null };
          console.log('Realtime UPDATE received:', updatedPaper);

          // Check if mark_scheme_processed_at was set (processing complete)
          if (updatedPaper.mark_scheme_processed_at) {
            console.log('Mark scheme processing complete!');
            cleanup();
            resolve();
          }
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
        if (status === 'CHANNEL_ERROR') {
          cleanup();
          reject(new Error('Failed to subscribe to updates. Please try again.'));
        }
      });
  });

  return {
    promise,
    rejectWithError: (error: Error) => {
      cleanup();
      if (rejectFn) rejectFn(error);
    },
    cleanup,
  };
};

const uploadMarkScheme = async (params: UploadMarkSchemeParams): Promise<UploadMarkSchemeResult> => {
  const markSchemeFilePath = generateMarkSchemeFilePath(params.pdfUrl);

  // Upload mark scheme PDF to storage
  const { error: uploadError } = await supabaseClient.storage
    .from('past-papers')
    .upload(markSchemeFilePath, params.file, { upsert: true });

  if (uploadError) {
    throw new Error(`Failed to upload mark scheme PDF: ${uploadError.message}`);
  }

  console.log('Mark scheme PDF uploaded to:', markSchemeFilePath);

  // Set up Realtime subscription BEFORE calling the edge function
  const { promise: realtimePromise, rejectWithError, cleanup } = setupRealtimeWait(
    params.pastPaperId,
    REALTIME_TIMEOUT_MS
  );

  // Fire the edge function - pass error handler to reject Realtime promise on quick failures
  console.log('Calling process-mark-scheme Edge Function with pastPaperId:', params.pastPaperId);
  invokeEdgeFunction('process-mark-scheme', { pastPaperId: params.pastPaperId }, (error) => {
    // Edge function failed quickly (validation error, etc.)
    // Reject the Realtime promise so the UI shows the error
    rejectWithError(error);
  });

  try {
    // Wait for Realtime to notify us when mark scheme processing is complete
    console.log('Waiting for Realtime notification...');
    await realtimePromise;

    console.log('Mark scheme processed successfully!');

    return {
      pastPaperId: params.pastPaperId,
    };
  } catch (error) {
    cleanup();
    throw error;
  }
};

export const useUploadMarkScheme = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadMarkScheme,
    onSuccess: (data) => {
      console.log('Mark scheme processed successfully!', data);
      toast.success('Mark scheme processed successfully!');
      // Invalidate admin past paper query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['admin-past-paper', data.pastPaperId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to upload mark scheme. Please try again.');
    },
  });
};

export default useUploadMarkScheme;
